import React from "react";
import {
  Box,
  Button,
  IconButton,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { tokens } from "../../theme";
import { useDashboardNotifications } from "../../context/DashboardNotificationsContext";
import {
  getCategoryGradient,
  getCategoryMeta,
  getCategoryWash,
  isCriticalPriority,
} from "../../data/DashboardNotifications/notificationStyles";

const Banner = ({ message, onDismiss, reduceMotion }) => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const isDark = theme.palette.mode === "dark";
  const meta = getCategoryMeta(message.category);
  const critical = isCriticalPriority(message.priority);

  return (
    <Box
      role="status"
      aria-live="polite"
      sx={{
        display: "flex",
        alignItems: "center",
        gap: 1.5,
        px: 2,
        py: critical ? 1.4 : 1.1,
        background: getCategoryWash(meta, isDark),
        borderBottom: `2px solid ${meta.accent}`,
        boxShadow: critical
          ? `inset 0 0 0 1px ${meta.accent}66, 0 4px 16px ${meta.accent}40`
          : `0 2px 10px ${meta.accent}22`,
        position: "relative",
        overflow: "hidden",
        ...(reduceMotion
          ? {}
          : {
              animation: "dashboardBannerSlideIn 0.4s cubic-bezier(0.22, 1, 0.36, 1)",
              "@keyframes dashboardBannerSlideIn": {
                from: { transform: "translateY(-100%)", opacity: 0 },
                to: { transform: "translateY(0)", opacity: 1 },
              },
            }),
      }}
    >
      {critical && !reduceMotion && (
        <Box
          sx={{
            position: "absolute",
            inset: 0,
            background: `linear-gradient(90deg, transparent, ${meta.accent}30, transparent)`,
            backgroundSize: "200% 100%",
            animation: "dashboardBannerShimmer 2.2s linear infinite",
            "@keyframes dashboardBannerShimmer": {
              from: { backgroundPosition: "200% 0" },
              to: { backgroundPosition: "-200% 0" },
            },
            pointerEvents: "none",
          }}
        />
      )}

      <Box
        sx={{
          width: 32,
          height: 32,
          borderRadius: "50%",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          flexShrink: 0,
          background: getCategoryGradient(meta, isDark),
          boxShadow: `0 2px 8px ${meta.accent}55`,
          zIndex: 1,
          ...(critical && !reduceMotion
            ? {
                animation: "dashboardBannerPulseRing 1.6s ease-in-out infinite",
                "@keyframes dashboardBannerPulseRing": {
                  "0%, 100%": { boxShadow: `0 0 0 0 ${meta.accent}88` },
                  "50%": { boxShadow: `0 0 0 6px ${meta.accent}00` },
                },
              }
            : {}),
        }}
      >
        <meta.Icon sx={{ color: "#fff", fontSize: 18 }} />
      </Box>

      <Typography
        variant="body2"
        sx={{ flex: 1, color: isDark ? "#f1f5f9" : "#1e293b", zIndex: 1 }}
      >
        <strong>{message.title}</strong>
        {message.body ? ` — ${message.body}` : ""}
      </Typography>

      {message.ctaLabel && message.ctaUrl && (
        <Button
          size="small"
          variant="contained"
          href={message.ctaUrl}
          sx={{
            background: getCategoryGradient(meta, isDark),
            textTransform: "none",
            whiteSpace: "nowrap",
            fontWeight: 600,
            boxShadow: `0 2px 8px ${meta.accent}55`,
            zIndex: 1,
            "&:hover": { filter: "brightness(1.08)", boxShadow: `0 4px 12px ${meta.accent}66` },
          }}
        >
          {message.ctaLabel}
        </Button>
      )}
      {message.isDismissible && (
        <IconButton
          size="small"
          aria-label={`Dismiss notification: ${message.title}`}
          onClick={() => onDismiss(message.id)}
          sx={{ zIndex: 1, color: isDark ? "#f1f5f9" : "#334155" }}
        >
          <CloseIcon fontSize="small" />
        </IconButton>
      )}
    </Box>
  );
};

const NotificationBannerStack = () => {
  const { banners, dismiss } = useDashboardNotifications();
  const reduceMotion = useMediaQuery("(prefers-reduced-motion: reduce)");

  if (banners.length === 0) return null;

  return (
    <Box sx={{ display: "flex", flexDirection: "column" }}>
      {banners.map((message) => (
        <Banner
          key={message.id}
          message={message}
          onDismiss={dismiss}
          reduceMotion={reduceMotion}
        />
      ))}
    </Box>
  );
};

export default NotificationBannerStack;
